import { isFullyIslandedIntent } from '../intent/gridScope';
import { resolveWaterStressBand } from '../stage2/waterStressBand';
/**
 * Power & cooling answers for the Stakeholder Brief.
 *
 * Datacrate is Gateway Power's site-intelligence platform; Gateway Power
 * engineers, delivers and operates dedicated on-site (behind-the-meter) power
 * and cooling for AI data centers. The brief therefore leads with what a
 * developer, utility or investor asks first: the site's BTM architecture
 * (power strategy), power type, DC load, rack-cooling loop and cold-end heat
 * rejection — five short labelled lines, each with a few-word basis — and then
 * the power and cooling risks the platform already evidences, each with one
 * recommended action.
 *
 * Source precedence, stated on every line and never mixed:
 *   1. `recorded` — a choice on the site record: Project Intent (load, build
 *      phase power sources, rack cooling, heat rejection), the Intent tab's
 *      grid vs on-site strategy (or an accepted Stage 2 power decision), or
 *      the promoted Generation & Impact scenario. Economic screening inputs
 *      are a what-if and are never recorded.
 *   2. `recommended` — usable screening outcomes, then the deterministic
 *      option stances. A recommended size range retains its stated resource
 *      assumptions; it is never relabelled as a committed project load.
 *   3. `not-determined` — with where it closes (`openIn`): a Stage 1 gap
 *      carries the single next step; a Stage 2 decision field names the
 *      decision that settles it and is never a screening gap.
 *
 * Nothing here computes a new figure or invents a recommendation: every value
 * is a recorded field, a recommendation the platform already computes, or an
 * evidenced stance reason. Pure and deterministic for identical inputs (the
 * caller supplies `asOf`).
 */
import type { SiteEntity } from '../../types/site';
import type {
  BopHeatRejection,
  PhasePowerComponentKey,
  ProjectIntent,
  ProjectPhase,
  SiteRagReport,
} from '../../types/siteRag';
import type { EvidenceSection, SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteResourceView } from '../../types/siteResourceEvidence';
import { readSiteResourceView, siteResourceViewFromSed } from '../sed/siteResourceDocumentProjection';
import type { GenerationImpactScenarioSnapshot, GenerationTechnology } from '../../types/generationImpact';
import type { PerSourceAvailabilityResolution } from '../powerAvailability/resolvePerSourceAvailability';
import { effectivePower, formatPrimaryBtmForCover, humanizePowerMix } from '../projectIntent';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { assessCoolingOptions, screeningWaterRisks } from '../stage2/coolingStances';
import { assessPowerOptions, optionMissesPhaseDate, POWER_OPTION_LABEL } from '../stage2/powerStances';
import {
  coolingProposal,
  phasePowerProposal,
  STRATEGY_LABEL,
  strategyProposal,
} from '../stage2/deriveStage2Decisions';
import {
  addYears,
  campusLoad,
  datedPhases,
  EPA_DESIGNATION_EVIDENCE_PATH,
  phaseList,
  readEpaNonattainment,
  readOtrMembership,
  type PowerOptionId,
} from '../stage2/stage2Evidence';
import { STAGE2_SOURCES, type Stage2SourceId } from '../stage2/stage2Sources';
import type {
  BtmStrategy,
  Stage2OptionAssessment,
  StanceReason,
} from '../stage2/stage2Types';
import { CALL_READY_SPOKEN_STATE_TEMPLATES } from './callReadySpokenTemplates';
import { deriveScreeningArchitectureRecommendations } from './screeningArchitectureRecommendations';
import { CONTEXT_ONLY_QUEUE_TEXT } from '../gridQueueEvidence';
import type {
  CallReadyAnswerBasis,
  CallReadyBriefStage,
  CallReadyRecommendation,
  CallReadyRoute,
} from './callReadyFindings';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type PowerCoolingLineId =
  | 'btm-architecture'
  | 'power-type'
  | 'dc-power'
  | 'cooling-strategy'
  | 'cooling-type';

export type PowerCoolingLineBasis = 'recorded' | 'recommended' | 'not-determined';

/** One labelled line of the lead power & cooling answer. */
export interface PowerCoolingLine {
  id: PowerCoolingLineId;
  label: string;
  /** The recorded or recommended value; "Not yet determined" otherwise. */
  value: string;
  basis: PowerCoolingLineBasis;
  /** A few words: where the value comes from, or why it is recommended. */
  basisNote: string;
  /**
   * Where a line that is not (fully) determined closes. `early-screening`
   * makes it a Stage 1 gap (a screening intent key, the grid vs on-site
   * strategy, a site-evidence refresh or a utility letter) and it carries a
   * `nextStep`; `mature-validation` is a Stage 2 decision field, named in the
   * basis note and never counted as a screening gap.
   */
  openIn?: CallReadyBriefStage;
  route?: CallReadyRoute;
  spokenLabel?: string;
  settlesWith?: string;
  /** The single step that closes a Stage 1 gap, or completes a partly recorded line. */
  nextStep?: string;
  /** The platform's full reasoning, when longer than the basis note. */
  rationale?: string;
  /** Evidence reference ids (resolved to sources by the caller). */
  evidenceRefs: readonly PowerCoolingEvidenceRef[];
  /** The value as a spoken noun phrase, for the composed summary. */
  spoken?: string;
  /** Why a recommended cold end, as a spoken clause ("the basin is under High water stress"). */
  spokenReason?: string;
}

export type PowerCoolingRiskArea = 'btm' | 'power' | 'cooling' | 'site';

/** A power or cooling risk the platform evidences, or a topic not yet assessed. */
export interface PowerCoolingRisk {
  id: string;
  area: PowerCoolingRiskArea;
  title: string;
  state: 'evidenced' | 'not-assessed';
  severity: 'significant' | 'noted';
  /** What the evidence says (or why the topic is not assessed). */
  finding: string;
  /** Where the evidence comes from, in a few words. */
  source: string;
  /** One recommended action. */
  action: string;
  origin?: CallReadyBriefStage;
  route?: CallReadyRoute;
  spokenResponse?: string;
  evidenceRefs: readonly PowerCoolingEvidenceRef[];
}

/**
 * An evidence reference the caller resolves into a `CallReadyEvidenceSource`
 * (the SED/site/report source builders live beside the rest of the brief).
 */
export type PowerCoolingEvidenceRef =
  | { kind: 'site' }
  | { kind: 'scenario' }
  | { kind: 'sed'; section: PowerCoolingSedSection }
  | { kind: 'report'; reportId: string }
  | { kind: 'stage2-source'; sourceId: Stage2SourceId }
  | { kind: 'water-screen' };

export type PowerCoolingSedSection =
  | 'waterStress'
  | 'grid'
  | 'fuelSupply'
  | 'onSiteInfrastructure'
  | 'environmental'
  | 'flood'
  | 'seismic'
  | 'interconnection';

export interface PowerCoolingDryCapacityScreen {
  /** A published dry-design temperature screen exists for the site. */
  screened: boolean;
  /** At least one facility-water class qualifies for dry-only operation. */
  dryOnlyCandidate: boolean;
  peakDryBulbProxyC?: number;
  /** "W45 is …" status label, when the W45 class was checked. */
  w45Status?: string;
}

export interface PowerCoolingInputs {
  site: SiteEntity;
  /** Exact-current SED only (the brief's currency rule); null otherwise. */
  sed: SiteEvidenceDocument | null;
  /** Latest USABLE Stage 1 reports; held, failed or draft attempts are excluded. */
  // Narrative/evidence view only: a saved decision-analysis envelope is not a
  // helper input and cannot become authoritative through this presentation path.
  feasibility: Omit<SiteRagReport, 'decisionAnalysis'> | null;
  environmental: SiteRagReport | null;
  supplyChain?: SiteRagReport | null;
  testFit?: SiteRagReport | null;
  /**
   * The promoted Generation & Impact scenario, resolved by the caller against
   * Project Intent's promotion stamps (`resolvePromotedGenerationScenario`):
   * an unpromoted, stale or hash-mismatched snapshot is null here, because a
   * promoted scenario is a recorded choice.
   */
  promotedScenario: GenerationImpactScenarioSnapshot | null;
  availability: PerSourceAvailabilityResolution | null;
  studyAreaAcres: number | null;
  /** Today as an ISO date (lead-time rules). */
  asOf: string;
  /** Trusted (brief trust rules) SED sections the risks read directly. */
  trusted: {
    waterStress: EvidenceSection<NonNullable<SiteEvidenceDocument['waterStress']['data']>> | null;
    flood: EvidenceSection<NonNullable<SiteEvidenceDocument['flood']['data']>> | null;
    seismic: EvidenceSection<NonNullable<SiteEvidenceDocument['seismic']['data']>> | null;
    grid?: SiteEvidenceDocument['grid'] | null;
    interconnection?: SiteEvidenceDocument['interconnection'] | null;
  };
  dryCapacity: PowerCoolingDryCapacityScreen | null;
  /**
   * Which stage's gap rules and risk actions apply. The brief derives this
   * from a valid promoted scenario or grid-only supply after Stage 1.
   */
  stage?: CallReadyBriefStage;
}

export interface PowerCoolingAnswerSet {
  /** Current-SED or immutable report source statements; not adopted choices or capacity estimates. */
  resourceStatements?: SiteResourceView;
  lines: PowerCoolingLine[];
  risks: PowerCoolingRisk[];
  /** Topics screened with no evidenced risk. */
  clearTopics: string[];
  /** Answer bases the lines rest on (recorded → site fact, recommended → screening …). */
  lineBases: CallReadyAnswerBasis[];
  riskBases: CallReadyAnswerBasis[];
  /** The recorded-or-recommended cold end, when there is one. */
  coolingType: Exclude<BopHeatRejection, 'unknown'> | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary
// ─────────────────────────────────────────────────────────────────────────────

export const POWER_COOLING_LINE_LABEL: Readonly<Record<PowerCoolingLineId, string>> = {
  'btm-architecture': 'BTM architecture',
  'power-type': 'Power type',
  'dc-power': 'DC power',
  'cooling-strategy': 'Cooling strategy',
  'cooling-type': 'Cooling type',
};

/** Order the lines are presented in. */
const LINE_ORDER: readonly PowerCoolingLineId[] = [
  'btm-architecture',
  'power-type',
  'dc-power',
  'cooling-strategy',
  'cooling-type',
];

/**
 * Order the open lines are resolved in: every figure derives from the load,
 * then the architecture decides the supply, then the cold end sets water.
 */
export const POWER_COOLING_RESOLUTION_ORDER: readonly PowerCoolingLineId[] = [
  'dc-power',
  'btm-architecture',
  'power-type',
  'cooling-type',
  'cooling-strategy',
];

/** Rack-side loop (`coolingStrategy`) as the lines state it. */
export const COOLING_STRATEGY_VALUE: Readonly<Record<'air' | 'liquid' | 'hybrid', string>> = {
  air: 'Air cooling',
  liquid: 'Direct liquid cooling (CDU loops)',
  hybrid: 'Hybrid air + liquid cooling',
};

const COOLING_STRATEGY_SPOKEN: Readonly<Record<'air' | 'liquid' | 'hybrid', string>> = {
  air: 'air cooling',
  liquid: 'direct liquid cooling',
  hybrid: 'hybrid air-liquid cooling',
};

/** Cold-end heat rejection (`bopHeatRejection`) as the lines state it. */
export const HEAT_REJECTION_VALUE: Readonly<Record<Exclude<BopHeatRejection, 'unknown'>, string>> = {
  'dry-cooler': 'Dry coolers (closed loop)',
  'wet-cooling-tower': 'Evaporative cooling towers',
  hybrid: 'Hybrid wet/dry heat rejection',
  'once-through': 'Once-through water cooling',
  'absorption-cchp': 'Absorption chillers / CCHP (heat-driven)',
  'free-cooling': 'Free cooling (economiser)',
};

const HEAT_REJECTION_SPOKEN: Readonly<Record<Exclude<BopHeatRejection, 'unknown'>, string>> = {
  'dry-cooler': 'dry coolers',
  'wet-cooling-tower': 'evaporative cooling towers',
  hybrid: 'hybrid wet/dry heat rejection',
  'once-through': 'once-through water cooling',
  'absorption-cchp': 'absorption chillers (CCHP)',
  'free-cooling': 'free cooling',
};

const STRATEGY_SPOKEN: Readonly<Record<BtmStrategy, string>> = {
  'grid-only': 'a grid-only architecture',
  hybrid: 'a hybrid (grid + on-site generation) architecture',
  'btm-only': 'a behind-the-meter-only architecture',
  islanded: 'an islanded (off-grid) architecture',
};

const TECHNOLOGY_LABEL: Readonly<Record<GenerationTechnology, string>> = {
  'reciprocating-engine': 'Reciprocating gas engines',
  'simple-cycle-gas-turbine': 'Simple-cycle gas turbines',
  'combined-cycle-block': 'Combined-cycle gas',
  'grid-import': 'Grid import',
  'battery-storage': 'Battery storage',
  'solar-bess': 'Solar + storage',
  'wind-bess': 'Wind + storage',
  'nuclear-smr': 'Small modular reactors',
  'large-nuclear': 'Large nuclear',
};

const TECHNOLOGY_OPTION: Readonly<Record<GenerationTechnology, PowerOptionId | null>> = {
  'reciprocating-engine': 'recip',
  'simple-cycle-gas-turbine': 'gas-sc',
  'combined-cycle-block': 'gas-cc',
  'grid-import': 'grid',
  'battery-storage': null,
  'solar-bess': 'solar-bess',
  'wind-bess': 'wind-bess',
  'nuclear-smr': 'smr',
  'large-nuclear': 'large-nuclear',
};

/**
 * A supply option as its technology class, for a recommendation that names no
 * equipment ("Reciprocating gas engines", never a make, model or count).
 */
const OPTION_TECHNOLOGY_LABEL: Readonly<Record<PowerOptionId, string>> = {
  grid: 'Grid import (utility service)',
  'gas-cc': 'Combined-cycle gas',
  'gas-sc': 'Simple-cycle gas turbines',
  recip: 'Reciprocating gas engines',
  'solar-bess': 'Solar + storage',
  'wind-bess': 'Wind + storage',
  smr: 'Small modular reactors',
  'large-nuclear': 'Large nuclear',
};

/**
 * Phase component → supply option, for scoping the risks. A generic turbine
 * component names no heavy-duty combined-cycle block, so it scopes on-site
 * combustion without triggering the heavy-duty lead-time rule (that needs
 * `gas-cc` named explicitly).
 */
const COMPONENT_OPTIONS: Readonly<Record<PhasePowerComponentKey, readonly PowerOptionId[]>> = {
  recip: ['recip'],
  'gas-turbine': ['gas-sc'],
  battery: [],
  grid: ['grid'],
  'solar-bess': ['solar-bess'],
  'wind-bess': ['wind-bess'],
  smr: ['smr'],
  'large-nuclear': ['large-nuclear'],
};

const GAS_OPTIONS: ReadonlySet<PowerOptionId> = new Set(['gas-cc', 'gas-sc', 'recip']);

/**
 * Risk area headings. Grid capacity and interconnection are supply risks, not
 * "DC power"; air permits, gas and generation equipment belong to on-site
 * generation; water stress and dry capacity to cooling and water.
 */
export const RISK_AREA_LABEL: Readonly<Record<PowerCoolingRiskArea, string>> = {
  btm: 'On-site generation',
  power: 'Power supply',
  cooling: 'Cooling and water',
  site: 'Plant siting',
};

/** W8 — a spoken summary, template wrapper included, stays at or under this many words. */
export const POWER_COOLING_SPOKEN_WORD_LIMIT = 60;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function sortedPhases(intent: ProjectIntent | null | undefined): ProjectPhase[] {
  return [...(intent?.phases ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
}

function phaseHasTechnology(phase: ProjectPhase): boolean {
  return (phase.powerSourceConfig?.components.length ?? 0) > 0
    || (phase.powerSource != null && phase.powerSource !== 'undecided');
}

function phaseLabel(phase: ProjectPhase): string {
  return phase.label?.trim() || `Phase ${phase.sequence ?? '?'}`;
}

function phaseOptions(phase: ProjectPhase): PowerOptionId[] | null {
  const components = phase.powerSourceConfig?.components ?? [];
  if (components.length > 0) return components.flatMap(component => [...COMPONENT_OPTIONS[component]]);
  switch (phase.powerSource) {
    case 'grid':
    case 'gas-cc':
    case 'gas-sc':
    case 'smr':
    case 'large-nuclear':
    case 'solar-bess':
    case 'wind-bess':
      return [phase.powerSource];
    default:
      // 'hybrid' names no technology; 'undecided' none at all.
      return null;
  }
}

/** The WRI band label without its list-number prefix ("3. High (40-80%)" → "High (40-80%)"). */
function stressBandLabel(data: NonNullable<SiteEvidenceDocument['waterStress']['data']>): string {
  return resolveWaterStressBand(data).label;
}

/** Sentence case, no trailing full stop — for a few-word basis note. */
function note(text: string): string {
  const trimmed = text.trim().replace(/\.$/, '');
  return trimmed ? `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}` : trimmed;
}

/** Lower-case a leading word for use mid-sentence, but never an acronym ("BTM", "DC", "SMR"). */
export function lowerFirst(text: string): string {
  return /^[A-Z]{2}/.test(text) ? text : `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

/** "a", "b and c" / "a, b and c". */
export function joinList(items: readonly string[]): string {
  return items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/** A display value read aloud: mid-sentence case, and "→" between phases as ", then". */
function spokenPhrase(value: string): string {
  return lowerFirst(value).replace(/\s*→\s*/g, ', then ');
}

/**
 * "a" or "an" before a figure, by how it is read: 8, 11, 18, 80, 800, 11,000
 * and 18,000 take "an"; everything else takes "a".
 */
function indefiniteArticle(figure: string): 'a' | 'an' {
  const digits = /^[\d,]+/.exec(figure)?.[0].replaceAll(',', '') ?? '';
  if (digits.startsWith('8')) return 'an';
  if (digits.length % 3 === 2 && (digits.startsWith('11') || digits.startsWith('18'))) return 'an';
  return 'a';
}

/** The first sentence of a stance reason (its fact), for a compact risk line. */
function firstSentence(text: string): string {
  const match = /^(.+?[.!?])(?:\s+(?=[A-Z“"(])|$)/.exec(text.trim());
  return (match?.[1] ?? text).trim();
}

function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

function refsFromReasons(reasons: readonly StanceReason[]): PowerCoolingEvidenceRef[] {
  const refs: PowerCoolingEvidenceRef[] = [];
  for (const reason of reasons) {
    for (const sourceId of reason.sourceIds ?? []) refs.push({ kind: 'stage2-source', sourceId });
    const path = reason.evidencePath ?? '';
    if (path.startsWith('report:')) {
      const reportId = path.slice('report:'.length).split('.')[0];
      if (reportId) refs.push({ kind: 'report', reportId });
    } else if (path.startsWith('sed.')) {
      const section = path.slice('sed.'.length).split('.')[0] as PowerCoolingSedSection;
      if (['waterStress', 'grid', 'fuelSupply', 'environmental', 'flood', 'seismic', 'interconnection'].includes(section)) {
        refs.push({ kind: 'sed', section });
      }
    }
    if (reason.basis === 'owner-input') refs.push({ kind: 'site' });
  }
  return dedupeRefs(refs);
}

function refKey(ref: PowerCoolingEvidenceRef): string {
  switch (ref.kind) {
    case 'sed': return `sed:${ref.section}`;
    case 'report': return `report:${ref.reportId}`;
    case 'stage2-source': return `stage2:${ref.sourceId}`;
    default: return ref.kind;
  }
}

function dedupeRefs(refs: readonly PowerCoolingEvidenceRef[]): PowerCoolingEvidenceRef[] {
  const seen = new Set<string>();
  return refs.filter((ref) => {
    const key = refKey(ref);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Short source phrase for a stance reason: the cited publisher, else the record. */
function reasonSource(reason: StanceReason): string {
  const cited = (reason.sourceIds ?? [])
    .map(id => STAGE2_SOURCES[id])
    .filter(source => source.url)
    .map(source => source.publisher.replace(/\s*\(.*\)$/, ''));
  const unique = [...new Set(cited)];
  if (unique.length > 0) return unique.join(' · ');
  const path = reason.evidencePath ?? '';
  if (path.startsWith('report:')) return 'Current screening analysis';
  if (path.startsWith('sed.') || path.startsWith('availability.')) return 'Current site evidence (SED)';
  if (reason.basis === 'owner-input') return 'Project Intent';
  return 'Platform screening policy';
}

/** Where an air-permitting reason's designation comes from. */
function airSource(reason: StanceReason): string {
  if (reason.rule === 'ozone-transport') return 'Clean Air Act §184 (Ozone Transport Region)';
  const path = reason.evidencePath ?? '';
  if (path === EPA_DESIGNATION_EVIDENCE_PATH) return 'US EPA Green Book (county designations)';
  if (path.startsWith('report:')) return 'Current environmental screening (text)';
  if (path.startsWith('sed.siteResearch')) return 'Site research (text)';
  return reasonSource(reason);
}

/** The promoted scenario's unit technologies (defensive: legacy snapshots may lack an input). */
function scenarioTechnologies(scenario: GenerationImpactScenarioSnapshot): GenerationTechnology[] {
  const units = scenario.input?.units;
  return Array.isArray(units)
    ? units.map(unit => unit.technology).filter((technology): technology is GenerationTechnology => technology in TECHNOLOGY_LABEL)
    : [];
}

/** A seeded solver topology is a modeled assumption, not the owner's declared grid relationship. */
function scenarioGridConnection(scenario: GenerationImpactScenarioSnapshot) {
  const assumption = [...(scenario.input?.assumptions ?? [])].reverse().find(row => row.field === 'gridConnection');
  return assumption?.source === 'planning-default' ? undefined : scenario.input?.gridConnection;
}

function strategyFromScenario(scenario: GenerationImpactScenarioSnapshot): BtmStrategy | null {
  const technologies = new Set(scenarioTechnologies(scenario));
  if (technologies.size === 0) return null;
  const connection = scenarioGridConnection(scenario);
  const onSite = [...technologies].some(technology => technology !== 'grid-import' && technology !== 'battery-storage');
  const gridTied = technologies.has('grid-import') || connection === 'grid-connected';
  if (!onSite) return gridTied ? 'grid-only' : null;
  if (gridTied) return 'hybrid';
  return connection === 'islanded' ? 'islanded' : 'btm-only';
}

function scenarioTechnologyValue(scenario: GenerationImpactScenarioSnapshot): string | null {
  const labels = [...new Set(scenarioTechnologies(scenario).map(technology => TECHNOLOGY_LABEL[technology]))];
  if (labels.length === 0) return null;
  const connection = scenarioGridConnection(scenario);
  const qualifier = connection === undefined ? ' (grid relationship not established)' : connection === 'islanded'
    ? ' (islanded)'
    : connection === 'grid-connected' ? ' (grid-connected)' : '';
  return `${labels.join(' + ')}${qualifier}`;
}

function scenarioOptions(scenario: GenerationImpactScenarioSnapshot): PowerOptionId[] {
  return [...new Set(scenarioTechnologies(scenario)
    .map(technology => TECHNOLOGY_OPTION[technology])
    .filter((option): option is PowerOptionId => option != null))];
}

/** A recorded field whose value was accepted from a prior report's snapshot. */
function carriedFromPriorReport(
  intent: ProjectIntent | null | undefined,
  ...fields: ReadonlyArray<'coolingStrategy' | 'bopHeatRejection' | 'powerRequirement' | 'powerRequirementMW' | 'powerBasis'>
): boolean {
  return fields.some(field => intent?.fieldSource?.[field] === 'prior-report');
}

function notDetermined(
  id: PowerCoolingLineId,
  basisNote: string,
  closes: { openIn: 'early-screening'; nextStep: string } | { openIn: 'mature-validation' },
): PowerCoolingLine {
  return {
    id,
    label: POWER_COOLING_LINE_LABEL[id],
    value: 'Not yet determined',
    basis: 'not-determined',
    basisNote,
    ...closes,
    evidenceRefs: [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lines
// ─────────────────────────────────────────────────────────────────────────────

interface LineContext {
  inputs: PowerCoolingInputs;
  intent: ProjectIntent | null | undefined;
  recordedStrategy: BtmStrategy | undefined;
  powerOptions: Stage2OptionAssessment[];
  coolingOptions: Stage2OptionAssessment[];
  screeningRecommendations: ReturnType<typeof deriveScreeningArchitectureRecommendations>;
}

/** The caller has already checked report currency, identity and release holds.
 * Unknown Intent leaves these advisory outcomes open; it does not erase them. */
function screeningRecommendationLine(context: LineContext, id: PowerCoolingLineId): PowerCoolingLine | null {
  const suggestion = context.screeningRecommendations[id];
  if (!suggestion) return null;
  const constrained = (optionId: string) => context.powerOptions.some(option => option.optionId === optionId
    && (option.stance === 'constrained' || option.stance === 'excluded-by-evidence'));
  if (id === 'power-type' && suggestion.supplyOptions?.some(constrained)) return null;
  if (id === 'btm-architecture' && suggestion.strategy === 'grid-only' && constrained('grid')) return null;
  if ((id === 'btm-architecture' || id === 'power-type')
    && suggestion.fieldPath.startsWith('btmDetailedAnalysis') && !suggestion.supplyOptions
    && context.powerOptions.some(option => GAS_OPTIONS.has(option.optionId as PowerOptionId) && constrained(option.optionId))) return null;
  const range = id === 'dc-power'
    ? /([\d,.]+\s*(?:[–—-]|to)\s*[\d,.]+\s*MW\s+(?:of\s+)?(?:IT(?:\s+load|\s+demand)?|facility(?:\s+load|\s+demand)?))/i.exec(suggestion.value)?.[1]
    : undefined;
  const compactSpoken = id === 'dc-power' && range ? `a conditional ${range} screening range`
    : id === 'btm-architecture' ? suggestion.strategy ? `a conditional ${STRATEGY_SPOKEN[suggestion.strategy].replace(/^a /, '')}` : 'a conditional BTM supply path'
    : id === 'power-type' && suggestion.supplyOptions?.length ? technologyAlternatives(suggestion.supplyOptions)
    : id === 'cooling-strategy' && suggestion.coolingStrategy ? COOLING_STRATEGY_SPOKEN[suggestion.coolingStrategy]
    : id === 'cooling-type' && suggestion.heatRejection ? HEAT_REJECTION_SPOKEN[suggestion.heatRejection]
    : id === 'power-type' ? 'a conditional power-supply shortlist'
    : id === 'cooling-strategy' ? 'a conditional rack-cooling option' : 'a conditional heat-rejection option';
  return {
    id,
    label: POWER_COOLING_LINE_LABEL[id],
    value: suggestion.value,
    basis: 'recommended',
    basisNote: suggestion.basisNote,
    rationale: `Saved screening outcome (${suggestion.fieldPath}). Review the resource constraints before adopting this option.`,
    evidenceRefs: [{ kind: 'report', reportId: suggestion.reportId }],
    spoken: suggestion.spoken ?? lowerFirst(compactSpoken),
  };
}

/** Firm site-deliverable fuel supports a conditional BTM shortlist even when
 * no load or grid letter exists. It does not establish campus MW or islanding. */
function fuelSupportedOptions(context: LineContext): Stage2OptionAssessment[] {
  const pool = context.inputs.availability?.sharedConstraints.find(item => item.id === 'gas-fuel-pool');
  if (!pool || pool.basis !== 'sed-firm-pipeline' || !Number.isFinite(pool.capMW) || pool.capMW <= 0) return [];
  return context.powerOptions.filter(option => GAS_OPTIONS.has(option.optionId as PowerOptionId)
    && (option.stance === 'viable' || option.stance === 'favoured'));
}

interface StrategyResolution {
  line: PowerCoolingLine;
  strategy: BtmStrategy | null;
}

/**
 * Recorded: the Intent-tab strategy, then the promoted scenario. Recommended:
 * usable screening outcomes, then the Stage 2 strategy proposal. A utility
 * letter settles grid reliance, not whether BTM can be screened.
 */
function btmArchitectureLine(context: LineContext): StrategyResolution {
  const { inputs, recordedStrategy, powerOptions } = context;
  const id = 'btm-architecture' as const;
  const label = POWER_COOLING_LINE_LABEL[id];
  if (recordedStrategy) {
    const accepted = inputs.site.stage2DecisionReview?.accepted?.['power-supply'];
    return {
      strategy: recordedStrategy,
      line: {
        id,
        label,
        value: STRATEGY_LABEL[recordedStrategy],
        basis: 'recorded',
        basisNote: accepted ? 'Accepted Stage 2 power decision' : 'Grid vs on-site strategy on the Intent tab',
        evidenceRefs: [{ kind: 'site' }],
        spoken: STRATEGY_SPOKEN[recordedStrategy],
      },
    };
  }
  const scenario = inputs.promotedScenario;
  const scenarioStrategy = scenario ? strategyFromScenario(scenario) : null;
  if (scenario && scenarioStrategy) {
    return {
      strategy: scenarioStrategy,
      line: {
        id,
        label,
        value: STRATEGY_LABEL[scenarioStrategy],
        basis: 'recorded',
        basisNote: `Promoted scenario “${scenario.name}”`,
        evidenceRefs: [{ kind: 'scenario' }],
        spoken: STRATEGY_SPOKEN[scenarioStrategy],
      },
    };
  }
  const screened = screeningRecommendationLine(context, id);
  if (screened) {
    return { strategy: context.screeningRecommendations[id]?.strategy ?? null, line: screened };
  }
  const proposal = strategyProposal(powerOptions, inputs.feasibility);
  if (proposal?.kind === 'btm-strategy') {
    const why = proposal.summary.includes('—') ? proposal.summary.split('—').slice(1).join('—') : proposal.summary;
    return {
      strategy: proposal.value,
      line: {
        id,
        label,
        value: STRATEGY_LABEL[proposal.value],
        basis: 'recommended',
        basisNote: note(why),
        rationale: proposal.reasons.map(reason => reason.text).join(' '),
        evidenceRefs: refsFromReasons(proposal.reasons),
        spoken: STRATEGY_SPOKEN[proposal.value],
      },
    };
  }
  if (fuelSupportedOptions(context).length > 0) {
    return {
      strategy: null,
      line: {
        id, label, basis: 'recommended',
        value: 'BTM-led generation; assess grid connection separately',
        basisNote: 'Documented site-deliverable gas supports a conditional on-site supply path',
        rationale: 'Compare on-site generation against usable land, cooling duty and permitting. Grid service is a separate option; no islanded design or campus capacity is assumed.',
        nextStep: 'Validate the BTM footprint, cooling duty and permitting; confirm utility capacity only for the grid-import option.',
        evidenceRefs: [{ kind: 'sed', section: 'onSiteInfrastructure' }, { kind: 'sed', section: 'fuelSupply' }],
        spoken: 'a conditional BTM-led architecture',
      },
    };
  }
  return {
    strategy: null,
    line: notDetermined(
      id,
      'No site-resource architecture recommendation is available',
      {
        openIn: 'early-screening',
        nextStep: 'Run or review early screening using usable site area, fuel access and water availability to compare BTM and grid-supply paths.',
      },
    ),
  };
}

interface PowerTypeResolution {
  line: PowerCoolingLine;
  /** Supply options in scope for the risks; null when the power type is open. */
  options: PowerOptionId[] | null;
  /** Recorded per-phase options, so a lead-time risk names only the phases that use the option. */
  phaseOptions?: ReadonlyMap<string, PowerOptionId[] | null>;
}

/** Technology classes, first capitalised, alternatives joined with "or". */
function technologyAlternatives(options: readonly PowerOptionId[]): string {
  const labels = [...new Set(options.map(option => OPTION_TECHNOLOGY_LABEL[option]))];
  return labels.map((label, index) => (index === 0 ? label : lowerFirst(label))).join(' or ');
}

/**
 * Recorded: Project Intent build phases, a recorded grid-only strategy, then
 * the promoted scenario's technologies. Recommended: the Stage 2 phase-power
 * proposal, a recommended grid-only strategy, then the favoured on-site
 * stances as technology classes. Otherwise a Stage 2 decision field — never a
 * screening gap.
 */
function powerTypeLine(context: LineContext, strategy: StrategyResolution): PowerTypeResolution {
  const { inputs, intent, recordedStrategy, powerOptions } = context;
  const id = 'power-type' as const;
  const label = POWER_COOLING_LINE_LABEL[id];
  const phases = sortedPhases(intent);
  const decided = phases.filter(phaseHasTechnology);
  if (decided.length > 0) {
    const undecided = phases.filter(phase => !phaseHasTechnology(phase));
    const options = decided.map(phaseOptions);
    const value = (intent ? formatPrimaryBtmForCover(intent) : null) ?? decided.map(humanizePowerMix).join(' → ');
    return {
      options: options.some(list => list == null) ? null : [...new Set(options.flat() as PowerOptionId[])],
      phaseOptions: new Map(phases.map(phase => [phase.id, phaseHasTechnology(phase) ? phaseOptions(phase) : null])),
      line: {
        id,
        label,
        value,
        basis: 'recorded',
        basisNote: undecided.length === 0
          ? 'Project Intent build phases'
          : `Recorded for ${decided.length} of ${phases.length} phases`,
        ...(undecided.length > 0
          ? { openIn: 'mature-validation' as const, nextStep: `Record the power source for ${undecided.map(phaseLabel).join(', ')}.` }
          : {}),
        evidenceRefs: [{ kind: 'site' }],
        spoken: spokenPhrase(value),
      },
    };
  }
  if (recordedStrategy === 'grid-only') {
    return {
      options: ['grid'],
      line: {
        id,
        label,
        value: 'Grid import (utility service)',
        basis: 'recorded',
        basisNote: 'Follows the recorded grid-only strategy',
        evidenceRefs: [{ kind: 'site' }],
        spoken: 'grid import (utility service)',
      },
    };
  }
  const scenario = inputs.promotedScenario;
  const scenarioValue = scenario ? scenarioTechnologyValue(scenario) : null;
  if (scenario && scenarioValue) {
    return {
      options: scenarioOptions(scenario),
      line: {
        id,
        label,
        value: scenarioValue,
        basis: 'recorded',
        basisNote: `Promoted scenario “${scenario.name}”`,
        evidenceRefs: [{ kind: 'scenario' }],
        spoken: spokenPhrase(scenarioValue),
      },
    };
  }
  const screened = screeningRecommendationLine(context, id);
  if (screened && context.screeningRecommendations[id]?.fieldPath.startsWith('btmDetailedAnalysis')) {
    return { options: context.screeningRecommendations[id]?.supplyOptions ?? null, line: screened };
  }
  const proposal = phasePowerProposal(
    intent,
    powerOptions,
    inputs.feasibility,
    recordedStrategy ?? strategy.strategy ?? undefined,
    inputs.asOf,
  );
  if (proposal?.kind === 'phase-power') {
    const proposed = [...new Set(proposal.phases.flatMap(phase => phase.options))];
    const storage = proposal.phases.some(phase => phase.battery);
    return {
      options: proposed,
      line: {
        id,
        label,
        value: proposal.summary,
        basis: 'recommended',
        basisNote: 'Named by the screening pathway; not constrained by site evidence',
        rationale: proposal.reasons.map(reason => reason.text).join(' '),
        evidenceRefs: refsFromReasons(proposal.reasons),
        spoken: `${joinList(proposed.map(option => lowerFirst(OPTION_TECHNOLOGY_LABEL[option])))}${storage ? ' with storage' : ''}`,
      },
    };
  }
  if (strategy.strategy === 'grid-only') {
    return {
      options: ['grid'],
      line: {
        id,
        label,
        value: 'Grid import (utility service)',
        basis: 'recommended',
        basisNote: 'Follows the recommended grid-only strategy',
        evidenceRefs: strategy.line.evidenceRefs,
        spoken: 'grid import (utility service)',
      },
    };
  }
  const favoured = powerOptions.filter(option => option.optionId !== 'grid' && option.stance === 'favoured');
  if (favoured.length > 0) {
    const reasons = favoured.flatMap(option => option.reasons.filter(reason => reason.effect === 'favoured'));
    const value = technologyAlternatives(favoured.map(option => option.optionId as PowerOptionId));
    return {
      options: favoured.map(option => option.optionId as PowerOptionId),
      line: {
        id,
        label,
        value,
        basis: 'recommended',
        basisNote: 'Favoured by the Stage 2 option screen',
        rationale: reasons.map(reason => reason.text).join(' '),
        evidenceRefs: refsFromReasons(reasons),
        spoken: lowerFirst(value),
      },
    };
  }
  if (screened) return { options: context.screeningRecommendations[id]?.supplyOptions ?? null, line: screened };
  const fuelOptions = fuelSupportedOptions(context);
  if (fuelOptions.length > 0) {
    const options = fuelOptions.map(option => option.optionId as PowerOptionId);
    return {
      options,
      line: {
        id, label, basis: 'recommended',
        value: `Compare ${lowerFirst(technologyAlternatives(options))}`,
        basisNote: 'Conditional shortlist from documented site-deliverable fuel; equipment and duty remain to be selected',
        evidenceRefs: [{ kind: 'sed', section: 'onSiteInfrastructure' }, { kind: 'sed', section: 'fuelSupply' }],
        spoken: lowerFirst(technologyAlternatives(options)),
      },
    };
  }
  return {
    options: null,
    line: notDetermined(
      id,
      strategy.strategy == null ? 'Follows the BTM architecture' : 'Chosen with the Stage 2 power decision',
      { openIn: 'mature-validation' },
    ),
  };
}

function phasingNote(intent: ProjectIntent | null | undefined): string {
  const phases = sortedPhases(intent);
  if (phases.length < 2) return '';
  const finalDate = phases[phases.length - 1]?.targetInServiceDate?.trim();
  return ` · ${phases.length} phases${finalDate ? `, full build ${finalDate.slice(0, 10)}` : ''}`;
}

/**
 * "a 300 MW IT load in 3 phases" — the load as a presenter says it. "DC power"
 * is never spoken (it can be heard as direct current); a load with no IT or
 * facility basis is "a … data center load".
 */
function loadSpoken(displayLabel: string, basis: 'it-load' | 'facility' | null, phaseCount: number): string {
  const figure = displayLabel.replace(/\s*\(legacy\)$/, '');
  const noun = basis === 'it-load' ? 'IT load' : basis === 'facility' ? 'facility load' : 'data center load';
  const phrase = /^\d/.test(figure)
    ? `${indefiniteArticle(figure)} ${figure} ${noun}`
    : `${basis === 'it-load' ? 'an' : 'a'} ${noun} ${lowerFirst(figure)}`;
  return `${phrase}${phaseCount >= 2 ? ` in ${phaseCount} phases` : ''}`;
}

/**
 * Recorded load comes from Intent. An explicitly recommended screening range
 * is an advisory sizing envelope, not the customer's committed load. Economic
 * what-if inputs never supply either tier.
 */
function dcPowerLine(context: LineContext): PowerCoolingLine {
  const { intent } = context;
  const id = 'dc-power' as const;
  const label = POWER_COOLING_LINE_LABEL[id];
  const power = effectivePower(intent);
  if (power.bucket !== 'undecided') {
    const basis = intent?.powerBasis === 'it-load' || intent?.powerBasis === 'facility' ? intent.powerBasis : null;
    const basisText = basis === 'it-load' ? ' IT load' : basis === 'facility' ? ' facility demand' : ' (basis not recorded)';
    return {
      id,
      label,
      value: `${power.displayLabel}${basisText}${phasingNote(intent)}`,
      basis: 'recorded',
      basisNote: carriedFromPriorReport(intent, 'powerRequirement', 'powerRequirementMW', 'powerBasis')
        ? 'Project Intent (carried from a prior analysis)'
        : 'Project Intent',
      ...(basis == null
        ? { openIn: 'early-screening' as const, nextStep: 'Record whether the load is IT or facility demand in Project Intent.' }
        : {}),
      evidenceRefs: [{ kind: 'site' }],
      spoken: loadSpoken(power.displayLabel, basis, sortedPhases(intent).length),
    };
  }
  const screened = screeningRecommendationLine(context, id);
  if (screened) return { ...screened, label: 'DC size range' };
  return notDetermined(
    id,
    isIntentFieldExplicitlyUnknown(intent, 'powerRequirement') ? 'Recorded as unknown' : 'No load recorded',
    {
      openIn: 'early-screening',
      nextStep: 'Screen a DC size range using usable land, water availability and fuel access; state whether the range is IT or facility demand.',
    },
  );
}

/**
 * Recorded: Project Intent's rack-cooling loop. Recommended: the Stage 2
 * cooling proposal's loop (AI training → liquid, AI inference → hybrid).
 * Otherwise a Stage 1 gap only while the workload profile (a screening key)
 * is missing; with a workload recorded it is a Stage 2 cooling decision.
 */
function coolingStrategyLine(context: LineContext): PowerCoolingLine {
  const { intent, coolingOptions } = context;
  const id = 'cooling-strategy' as const;
  const label = POWER_COOLING_LINE_LABEL[id];
  const recorded = intent?.coolingStrategy;
  if (recorded && recorded !== 'undecided') {
    return {
      id,
      label,
      value: COOLING_STRATEGY_VALUE[recorded],
      basis: 'recorded',
      basisNote: carriedFromPriorReport(intent, 'coolingStrategy')
        ? 'Project Intent rack-cooling loop (carried from a prior analysis)'
        : 'Project Intent rack-cooling loop',
      evidenceRefs: [{ kind: 'site' }],
      spoken: COOLING_STRATEGY_SPOKEN[recorded],
    };
  }
  const proposal = coolingProposal(intent, coolingOptions);
  if (proposal?.kind === 'cooling' && proposal.coolingStrategy) {
    const loop = coolingOptions.find(option => option.optionId === `loop:${proposal.coolingStrategy}`);
    const reasons = (loop?.reasons ?? []).filter(reason => reason.effect === 'favoured');
    return {
      id,
      label,
      value: COOLING_STRATEGY_VALUE[proposal.coolingStrategy],
      basis: 'recommended',
      basisNote: note(reasons[0]?.text ?? 'Favoured by the Stage 2 cooling screen'),
      evidenceRefs: [{ kind: 'site' }, ...refsFromReasons(reasons)],
      spoken: COOLING_STRATEGY_SPOKEN[proposal.coolingStrategy],
    };
  }
  const screened = screeningRecommendationLine(context, id);
  if (screened) return screened;
  if (isIntentFieldExplicitlyUnknown(intent, 'coolingStrategy')) {
    return notDetermined(id, 'Recorded as unknown', { openIn: 'mature-validation' });
  }
  const workloadOpen = !intent?.workloadProfile && !isIntentFieldExplicitlyUnknown(intent, 'workloadProfile');
  return workloadOpen
    ? notDetermined(id, 'Waits on the workload profile', {
        openIn: 'early-screening',
        nextStep: 'Record the workload profile; screening recommends rack cooling from its density.',
      })
    : notDetermined(id, 'Chosen with the Stage 2 cooling decision', { openIn: 'mature-validation' });
}

/**
 * Recorded: Project Intent's cold end. Recommended: the Stage 2 cooling
 * proposal's sink — dry coolers where WRI water stress is High, Extremely High
 * or arid; wet towers only at Low stress with permitted water rights. Otherwise
 * a Stage 1 gap only while no trusted water-stress screen is on the record.
 */
function coolingTypeLine(context: LineContext): { line: PowerCoolingLine; value: Exclude<BopHeatRejection, 'unknown'> | null } {
  const { inputs, intent, coolingOptions } = context;
  const id = 'cooling-type' as const;
  const label = POWER_COOLING_LINE_LABEL[id];
  const recorded = intent?.bopHeatRejection;
  if (recorded && recorded !== 'unknown') {
    return {
      value: recorded,
      line: {
        id,
        label,
        value: HEAT_REJECTION_VALUE[recorded],
        basis: 'recorded',
        basisNote: carriedFromPriorReport(intent, 'bopHeatRejection')
          ? 'Project Intent cold-end heat rejection (carried from a prior analysis)'
          : 'Project Intent cold-end heat rejection',
        evidenceRefs: [{ kind: 'site' }],
        spoken: HEAT_REJECTION_SPOKEN[recorded],
      },
    };
  }
  const proposal = coolingProposal(intent, coolingOptions);
  if (proposal?.kind === 'cooling' && proposal.bopHeatRejection) {
    const sink = proposal.bopHeatRejection;
    const favouredReasons = (coolingOptions.find(option => option.optionId === `sink:${sink}`)?.reasons ?? [])
      .filter(reason => reason.effect === 'favoured');
    const wetConstraint = coolingOptions
      .find(option => option.optionId === 'sink:wet-cooling-tower')
      ?.reasons.find(reason => reason.effect === 'constrained' && reason.evidencePath === 'sed.waterStress');
    const stress = inputs.trusted.waterStress?.data;
    const stressLabel = stress ? stressBandLabel(stress) : null;
    const arid = stress?.bwsCategory === -1;
    // Stage 2 stance language: water stress CONSTRAINS evaporative cooling;
    // `excluded-by-evidence` is reserved for a binding prohibition.
    const stressDriven = wetConstraint != null && sink !== 'wet-cooling-tower';
    const basisNote = stressDriven
      ? arid
        ? 'Arid basin (WRI Aqueduct) constrains evaporative cooling'
        : `${note(stressLabel ?? 'High')} water stress (WRI Aqueduct) constrains wet towers`
      : sink === 'wet-cooling-tower'
        ? 'Low water stress and permitted water rights'
        : 'Favoured by the Stage 2 cooling screen';
    const spokenReason = stressDriven
      ? arid
        ? 'the basin is arid'
        : `the basin is under ${(stressLabel ?? 'High').replace(/\s*\(.*$/, '')} water stress`
      : sink === 'wet-cooling-tower'
        ? 'basin water stress is low and water rights are permitted'
        : undefined;
    const reasons = [...(stressDriven && wetConstraint ? [wetConstraint] : []), ...favouredReasons];
    return {
      value: sink,
      line: {
        id,
        label,
        value: HEAT_REJECTION_VALUE[sink],
        basis: 'recommended',
        basisNote,
        rationale: reasons.map(reason => reason.text).join(' '),
        evidenceRefs: refsFromReasons(reasons),
        spoken: HEAT_REJECTION_SPOKEN[sink],
        ...(spokenReason ? { spokenReason } : {}),
      },
    };
  }
  const screened = screeningRecommendationLine(context, id);
  if (screened) return { value: context.screeningRecommendations[id]?.heatRejection ?? null, line: screened };
  if (isIntentFieldExplicitlyUnknown(intent, 'bopHeatRejection')) {
    return { value: null, line: notDetermined(id, 'Recorded as unknown', { openIn: 'mature-validation' }) };
  }
  return {
    value: null,
    line: inputs.trusted.waterStress != null
      ? notDetermined(id, 'Water stress does not settle wet vs dry; chosen with the Stage 2 cooling decision', {
          openIn: 'mature-validation',
        })
      : notDetermined(id, 'No basin water-stress screen', {
          openIn: 'early-screening',
          nextStep: 'Refresh the site evidence to load the WRI Aqueduct water-stress screen.',
        }),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Risks
// ─────────────────────────────────────────────────────────────────────────────

interface RiskContext {
  inputs: PowerCoolingInputs;
  intent: ProjectIntent | null | undefined;
  stage: CallReadyBriefStage;
  strategy: BtmStrategy | null;
  supplyOptions: PowerOptionId[] | null;
  phaseOptions: ReadonlyMap<string, PowerOptionId[] | null> | null;
  powerOptions: Stage2OptionAssessment[];
  coolingOptions: Stage2OptionAssessment[];
  coolingType: Exclude<BopHeatRejection, 'unknown'> | null;
}

interface RiskResult {
  risks: PowerCoolingRisk[];
  clear: string[];
}

/**
 * The action a risk carries in each stage. During early screening the action
 * shapes the power or cooling decision; vendor, OEM and delivery-slot checks
 * belong to mature validation, once equipment is being selected.
 */
function stageAction(context: RiskContext, actions: { earlyScreening: string; matureValidation: string }): string {
  return context.stage === 'mature-validation' ? actions.matureValidation : actions.earlyScreening;
}

function evidenced(
  risk: Omit<PowerCoolingRisk, 'state' | 'evidenceRefs'> & { evidenceRefs?: readonly PowerCoolingEvidenceRef[] },
): PowerCoolingRisk {
  return { ...risk, state: 'evidenced', evidenceRefs: dedupeRefs(risk.evidenceRefs ?? []) };
}

function unassessed(
  risk: Omit<PowerCoolingRisk, 'state' | 'severity' | 'evidenceRefs'>,
): PowerCoolingRisk {
  return { ...risk, state: 'not-assessed', severity: 'noted', evidenceRefs: [] };
}

/**
 * The campus is EXPLICITLY declared islanded: every build phase records an
 * islanded connection (`powerSourceConfig.gridConnection === 'islanded'`, the
 * Project Intent connection mode) with no grid component. A promoted scenario
 * does not count: its connection is usually the planning default
 * (`buildPlanningScenario` seeds 'islanded' until the owner declares the grid
 * relationship), and an owner choice made in the workbench is synced into the
 * phase anyway. A behind-the-meter strategy alone is a meter arrangement, not
 * an islanding declaration — BTM generation can run in parallel with the grid
 * — so its grid service is still screened.
 */
/** Grid rows remain in scope for conflicting or mixed grid-connected phases. */
function gridRowsOutOfScope(context: RiskContext): boolean {
  return isFullyIslandedIntent(context.intent, context.strategy);
}

function gridCapacityRisk(context: RiskContext, out: RiskResult): void {
  if (gridRowsOutOfScope(context)) return;
  const grid = context.powerOptions.find(option => option.optionId === 'grid');
  const documented = grid?.reasons.find(reason => reason.evidencePath === 'sed.grid.capacityDetail');
  if (documented?.effect === 'constrained') {
    out.risks.push(evidenced({
      id: 'grid-capacity',
      area: 'power',
      title: 'Grid capacity below the declared load',
      severity: 'significant',
      finding: documented.text,
      source: 'Current site evidence (utility capacity detail)',
      action: 'Size behind-the-meter generation for the shortfall (hybrid), or confirm a phased utility upgrade in writing.',
      evidenceRefs: [{ kind: 'sed', section: 'grid' }],
    }));
    return;
  }
  // Only a utility study or executed agreement covering the load is favoured;
  // an indication, a user-authored figure or posted ATC never clears it.
  if (documented?.effect === 'favoured') {
    out.clear.push('grid capacity');
    return;
  }
  out.risks.push(unassessed(documented && campusLoad(context.intent)
    ? {
        id: 'grid-capacity',
        area: 'power',
        title: 'Grid capacity',
        finding: documented.text,
        source: 'Current site evidence (utility capacity detail)',
        action: 'Obtain a utility study or executed service agreement for the declared load, covering import capacity during generation outages, upgrades and standby terms.',
      }
    : documented
    ? {
        id: 'grid-capacity',
        area: 'power',
        title: 'Grid capacity',
        finding: 'Utility capacity is documented, but the declared load is not, so coverage cannot be judged.',
        source: 'Current site evidence (utility capacity detail)',
        action: 'Record the project load so the documented utility capacity can be compared with it.',
      }
    : {
        id: 'grid-capacity',
        area: 'power',
        title: 'Grid capacity',
        finding: 'No utility capacity is documented; proximity or a voltage-class proxy is not a utility commitment.',
        source: 'Current site evidence',
        action: 'Request a utility capacity or will-serve letter with the deliverable MW and date.',
      }));
}

function interconnectionRisk(context: RiskContext, out: RiskResult): void {
  if (gridRowsOutOfScope(context)) return;
  // These sections have passed the same exact-current, independent-source
  // trust boundary as the rest of the brief. A regional generator benchmark,
  // a map proxy or an unscoped legacy duration cannot set load-service timing.
  const interconnection = context.inputs.trusted.interconnection;
  const grid = context.inputs.trusted.grid;
  const days = interconnection?.data?.queueDurationP50Days;
  const gridEstimate = grid?.data?.estimatedQueueTime?.trim();
  const timing = interconnection?.data?.queueDurationBasis === 'site-load-service'
    && typeof days === 'number' && Number.isFinite(days) && days > 0
      ? { value: `${days.toLocaleString('en-US')} days (P50 estimate)`, section: 'interconnection' as const,
          source: interconnection.source }
      : grid?.data?.estimatedQueueTimeBasis === 'site-load-service' && gridEstimate
        && !CONTEXT_ONLY_QUEUE_TEXT.test(gridEstimate)
        && !/\b(?:unknown|unavailable|unconfirmed|not established|not available)\b/i.test(gridEstimate)
        ? { value: gridEstimate, section: 'grid' as const, source: grid.source }
        : null;
  if (timing) {
    out.risks.push(evidenced({
      id: 'interconnection', area: 'power', title: 'Utility load-service timing', severity: 'noted',
      finding: `The current site-specific utility load-service estimate is ${timing.value}. It is an estimate, not a confirmed energization date.`,
      source: `Current site evidence · ${timing.source || 'Utility load-service estimate'}`,
      action: 'Confirm the applicable utility service milestones and compare this estimate with any declared phase dates; assess BTM alternatives against their own fuel, delivery and permitting evidence.',
      evidenceRefs: [{ kind: 'sed', section: timing.section }],
    }));
    return;
  }
  const queue = context.intent?.interconnectionStatus?.queueStatus
    ?? interconnection?.data?.queueStatus
    ?? null;
  if (queue === 'pre-application') {
    out.risks.push(evidenced({
      id: 'interconnection',
      area: 'power',
      title: 'Interconnection queue position',
      severity: 'significant',
      finding: 'The recorded interconnection request is pre-application. Its applicability to utility load service and the site energization date are not established.',
      source: 'Project Intent',
      action: 'Confirm the applicable utility load-service process and milestones; screen BTM alternatives independently using site fuel, land, water, delivery and permitting evidence.',
      evidenceRefs: [{ kind: 'site' }],
    }));
    return;
  }
  if (queue === 'queued' || queue === 'study') {
    out.risks.push(evidenced({
      id: 'interconnection',
      area: 'power',
      title: 'Interconnection queue position',
      severity: 'noted',
      finding: `The recorded interconnection request is ${queue === 'queued' ? 'queued' : 'in study'}. This status alone does not establish the site load-service timeline.`,
      source: 'Project Intent',
      action: 'Confirm which utility service process this status describes and obtain its site-specific milestones; compare conditional BTM options on their own evidence.',
      evidenceRefs: [{ kind: 'site' }],
    }));
    return;
  }
  if (queue === 'executed-load-service') {
    const fromIntent = context.intent?.interconnectionStatus?.queueStatus === queue;
    out.risks.push(evidenced({
      id: 'interconnection', area: 'power', title: 'Utility load-service delivery', severity: 'noted',
      finding: 'A customer load-service agreement is recorded as accepted. Procurement, service works, protection tests and commissioning remain separate delivery milestones; agreement acceptance does not establish energization or a generator LGIA.',
      source: fromIntent ? 'Project Intent' : 'Current site evidence · declared utility service status',
      action: 'Track the agreed service date against procurement, service works and commissioning. Assess any separate generator or nuclear interconnection request on its own evidence.',
      evidenceRefs: fromIntent ? [{ kind: 'site' }] : [{ kind: 'sed', section: 'interconnection' }],
    }));
    return;
  }
  if (queue === 'executed-LGIA') {
    // A generator interconnection agreement is not load service: it clears
    // only itself, and the campus load and standby service stay open.
    out.clear.push('generator interconnection agreement (load and standby service assessed separately)');
    const fromIntent = context.intent?.interconnectionStatus?.queueStatus === queue;
    out.risks.push(unassessed({
      id: 'interconnection',
      area: 'power',
      title: 'Utility load-service position',
      finding: 'An executed generator interconnection agreement (LGIA) is recorded. It does not establish utility load service or standby service for the campus load, and no load-service agreement is recorded.',
      source: fromIntent ? 'Project Intent' : 'Current site evidence · declared interconnection status',
      action: 'Confirm the utility load-service and standby-service position for the campus load; a generator LGIA does not establish it.',
    }));
    return;
  }
  out.risks.push(unassessed({
    id: 'interconnection',
    area: 'power',
    title: 'Interconnection queue position',
    finding: 'No interconnection position is recorded.',
    source: 'Project Intent',
    action: 'Record the interconnection position in Project Intent, or confirm with the utility that no request exists yet.',
  }));
}

/**
 * On-site PRIMARY combustion is in scope for the air and gas-supply rows. A
 * grid-only supply has none, but it does not settle standby or emergency
 * combustion: `standbyAirPermittingRisk` screens that separately.
 */
function combustionInScope(context: RiskContext): boolean {
  if (context.strategy === 'grid-only') return false;
  return context.supplyOptions == null || context.supplyOptions.some(option => GAS_OPTIONS.has(option));
}

/**
 * A grid-only campus still carries standby or emergency generators unless the
 * record says otherwise, and no Project Intent field records their absence.
 * Standby combustion is therefore 'declared' (the selected grid-led standby
 * plan) or unknown, and its air permit pathway is never screened clear here.
 */
function standbyAirPermittingRisk(context: RiskContext, out: RiskResult): void {
  if (context.strategy !== 'grid-only') return;
  const declared = context.inputs.site.generationAssessmentSelection?.kind === 'grid-led-standby-planning';
  out.risks.push(unassessed({
    id: 'standby-air-permitting',
    area: 'btm',
    title: 'Air permitting for standby generators',
    finding: declared
      ? 'The selected grid-led standby plan declares emergency standby generation. Its air permit pathway (permit type, emergency or non-emergency duty and testing hours) is not assessed, and an emergency approval does not permit prime or peaking operation.'
      : 'A grid-only supply does not settle standby generation: whether the campus will run standby or emergency combustion generators is not recorded, so their air permit pathway is not assessed.',
    source: declared ? 'Grid-led standby planning (owner-declared)' : 'Project Intent',
    action: declared
      ? 'Confirm the standby generators\' air permit pathway and permitted operating duty with the state or local air agency.'
      : 'Record the standby generation plan (fuel, units and emergency or non-emergency duty), then confirm its air permit pathway with the state or local air agency.',
  }));
}

function airPermittingRisk(context: RiskContext, out: RiskResult): void {
  if (!combustionInScope(context)) return;
  const gas = context.powerOptions.find(option => option.optionId === 'recip');
  const constrained = (gas?.reasons ?? []).filter(reason => reason.basis === 'regulation' && reason.effect === 'constrained');
  if (constrained.length > 0) {
    const lead = constrained[0]!;
    const fromText = /not confirmed against the EPA Green Book/.test(lead.text);
    out.risks.push(evidenced({
      id: 'air-permitting',
      area: 'btm',
      title: 'Air permitting for on-site combustion',
      severity: 'significant',
      finding: `${firstSentence(lead.text)}${fromText && !/not confirmed against the EPA Green Book/.test(firstSentence(lead.text)) ? ' This is read from screening text, not confirmed against the EPA Green Book.' : ''} A new major combustion source there needs nonattainment NSR with emission offsets.`,
      source: airSource(lead),
      action: stageAction(context, {
        earlyScreening: 'Carry the nonattainment NSR path into the power decision: favour lower-emitting units or plan for emission offsets, and confirm the designation with the state air agency.',
        matureValidation: 'Size and control on-site generation to stay below the major-source threshold (tons-per-year potential to emit with enforceable limits, not MW), or plan nonattainment NSR with emission offsets; confirm with the state air agency.',
      }),
      evidenceRefs: refsFromReasons(constrained),
    }));
    return;
  }
  const conflict = gas?.reasons.find(reason => /conflict|disagree|contradict/i.test(reason.text));
  const reportConflict = context.inputs.feasibility?.developmentRisks?.find(risk => /air.*permit|permit.*air/i.test(risk.reason) && (risk.riskLevel === 'High' || /conflict|undetermined/i.test(risk.reason)));
  if (conflict || reportConflict) {
    out.risks.push(unassessed({ id: 'air-permitting', area: 'btm', title: 'Air permitting for on-site combustion',
      finding: conflict?.text ?? 'The Screening Assessment leaves air-permit applicability undetermined; its finding conflicts with a clear county designation.',
      source: 'Screening Assessment and current EPA designation',
      action: 'Resolve the designation and air-permit applicability with the state air agency before presenting a clear permitting position.' }));
    return;
  }
  const epa = context.inputs.sed?.environmental?.data?.naaqsDesignations;
  const epaNonattainment = readEpaNonattainment(epa);
  if (epaNonattainment === null && readOtrMembership(context.inputs.sed).status !== 'inside') {
    // No current listing settles only the county designation. Attainment
    // areas still need PSD, minor-source or state permits as the equipment,
    // fuel, duty and potential to emit require, so the air row stays open.
    out.clear.push('county attainment designation (EPA Green Book)');
    out.risks.push(unassessed({
      id: 'air-permitting',
      area: 'btm',
      title: 'Air permitting for on-site combustion',
      finding: 'The EPA Green Book lists no current ozone, PM2.5 or Serious PM-10 nonattainment area for the county, which settles only the designation screen. The air permit pathway depends on the equipment, fuel, operating duty and potential to emit (PTE), and is not yet assessed.',
      source: 'US EPA Green Book (county designations)',
      action: 'Confirm the air permit pathway (permit type, any enforceable emission limits and the permitted operating duty) with the state air agency before presenting a permitting position.',
    }));
    return;
  }
  out.risks.push(unassessed({
    id: 'air-permitting',
    area: 'btm',
    title: 'Air permitting for on-site combustion',
    finding: 'The county\'s EPA NAAQS designations are not on the current record.',
    source: 'Current site evidence',
    action: 'Confirm the county\'s current EPA Green Book ozone and PM2.5 designations before siting combustion units.',
  }));
}

function gasSupplyRisk(context: RiskContext, out: RiskResult): void {
  if (!combustionInScope(context)) return;
  const supply = context.intent?.fuelAccess?.supply;
  const pool = context.powerOptions
    .find(option => option.optionId === 'recip')
    ?.reasons.find(reason => (reason.evidencePath ?? '').startsWith('availability.') && reason.effect === 'constrained');
  if (supply === 'none') {
    out.risks.push(evidenced({
      id: 'gas-supply',
      area: 'btm',
      title: 'Gas supply for on-site generation',
      severity: 'significant',
      finding: 'The owner records no gas supply path for on-site generation.',
      source: 'Project Intent',
      action: 'Qualify a pipeline lateral or an alternative fuel before relying on gas-fired generation.',
      evidenceRefs: [{ kind: 'site' }],
    }));
    return;
  }
  if (pool) {
    out.risks.push(evidenced({
      id: 'gas-supply',
      area: 'btm',
      title: 'Gas supply for on-site generation',
      severity: 'significant',
      finding: pool.text,
      source: 'Documented firm gas deliverability',
      action: 'Contract firm transport for the full load, or reduce the gas-fired share of the supply.',
      evidenceRefs: [{ kind: 'sed', section: 'fuelSupply' }],
    }));
    return;
  }
  if (supply === 'lateral-required') {
    out.risks.push(evidenced({
      id: 'gas-supply',
      area: 'btm',
      title: 'Gas supply for on-site generation',
      severity: 'noted',
      finding: 'A pipeline lateral is required: a separately permitted, long-lead asset on the critical path.',
      source: 'Project Intent',
      action: 'Start lateral routing, permitting and firm-transport talks with the pipeline operator now; consider dual-fuel.',
      evidenceRefs: [{ kind: 'site' }],
    }));
    return;
  }
  if (supply === 'near-pipeline') {
    out.risks.push(evidenced({
      id: 'gas-supply',
      area: 'btm',
      title: 'Gas supply for on-site generation',
      severity: 'noted',
      finding: 'The site is recorded as near a pipeline, but firm transport is not yet contracted.',
      source: 'Project Intent',
      action: 'Request firm transport capacity and a delivery date from the pipeline operator.',
      evidenceRefs: [{ kind: 'site' }],
    }));
    return;
  }
  if (supply === 'firm-contracted') {
    out.clear.push('gas supply');
    return;
  }
  const pipelineKm = context.inputs.sed?.fuelSupply?.data?.nearestPipelineKm;
  out.risks.push(unassessed({
    id: 'gas-supply',
    area: 'btm',
    title: 'Gas supply for on-site generation',
    finding: typeof pipelineKm === 'number' && Number.isFinite(pipelineKm)
      ? `No gas supply path is recorded; the nearest mapped pipeline is ${pipelineKm.toFixed(1)} km away, which does not establish firm transport.`
      : 'No gas supply path is recorded.',
    source: 'Project Intent',
    action: 'Record the gas supply path (fuel access) in Project Intent and request firm transport availability.',
  }));
}

const LEAD_TIME_RULES: ReadonlyArray<{
  option: PowerOptionId;
  sourceId: Stage2SourceId;
  finding: (phases: string, verb: string, asOf: string) => string;
  /** The mature-validation action; early screening weighs the lead time in the power decision. */
  action: string;
}> = [
  {
    option: 'gas-cc',
    sourceId: 'ge-vernova-lead-times-2026',
    finding: (phases, verb, asOf) => `Heavy-duty gas turbine lead times are about three years, and ${phases} ${verb} operation before ${addYears(asOf, 3)}.`,
    action: 'Confirm turbine delivery slots against the phase dates, or re-sequence the early phases.',
  },
  {
    option: 'smr',
    sourceId: 'doe-clinch-river-smr-2025',
    finding: (phases, verb) => `The first North American SMR targets commercial operation by the end of 2029, and ${phases} ${verb} operation before 2030.`,
    action: 'Plan interim supply for the early phases and confirm the reactor vendor\'s schedule.',
  },
  {
    option: 'large-nuclear',
    sourceId: 'eia-vogtle-2024',
    finding: (phases, verb, asOf) => `Recent US large reactors took about eleven years to build, and ${phases} ${verb} operation before ${addYears(asOf, 11)}.`,
    action: 'Treat large nuclear as a later-phase source and plan interim supply.',
  },
];

function leadTimeRisks(context: RiskContext, out: RiskResult): void {
  const options = context.supplyOptions;
  if (!options) return;
  const dated = datedPhases(context.intent);
  if (dated.length === 0) return;
  for (const rule of LEAD_TIME_RULES) {
    if (!options.includes(rule.option)) continue;
    // With recorded phase technologies, only the phases that use the option
    // count; a recommended or scenario-level option applies campus-wide.
    const using = dated.filter(phase => {
      const recorded = context.phaseOptions?.get(phase.phaseId);
      return recorded == null || recorded.includes(rule.option);
    });
    const early = using.filter(phase => optionMissesPhaseDate(rule.option, phase.target, context.inputs.asOf));
    if (early.length === 0) continue;
    out.risks.push(evidenced({
      id: `lead-time:${rule.option}`,
      area: 'power',
      title: `Equipment lead time · ${POWER_OPTION_LABEL[rule.option]}`,
      severity: early.length === using.length ? 'significant' : 'noted',
      finding: rule.finding(phaseList(early), early.length === 1 ? 'targets' : 'target', context.inputs.asOf),
      source: `Project Intent · ${STAGE2_SOURCES[rule.sourceId].publisher.replace(/\s*\(.*\)$/, '')}`,
      action: stageAction(context, {
        earlyScreening: 'Weigh this lead time in the power decision: favour shorter-lead equipment for the early phases, or re-sequence them.',
        matureValidation: rule.action,
      }),
      evidenceRefs: [{ kind: 'site' }, { kind: 'stage2-source', sourceId: rule.sourceId }],
    }));
  }
}

function waterStressRisks(context: RiskContext, out: RiskResult): void {
  const section = context.inputs.trusted.waterStress;
  const data = section?.data;
  if (!data) {
    out.risks.push(unassessed({
      id: 'water-stress',
      area: 'cooling',
      title: 'Water stress for cooling',
      finding: 'No basin water-stress screen is on the current record.',
      source: 'Current site evidence',
      action: 'Refresh the site evidence to load the WRI Aqueduct water-stress screen.',
    }));
  } else {
    const { category, label } = resolveWaterStressBand(data);
    const high = category === -1 || (typeof category === 'number' && category >= 3) || (category == null && data.stressLevel === 'High');
    const mediumHigh = category === 2;
    const wetRecorded = context.coolingType === 'wet-cooling-tower' && context.intent?.bopHeatRejection === 'wet-cooling-tower';
    if (high) {
      const reason = context.coolingOptions
        .find(option => option.optionId === 'sink:wet-cooling-tower')
        ?.reasons.find(candidate => candidate.evidencePath === 'sed.waterStress' && candidate.effect === 'constrained');
      out.risks.push(evidenced({
        id: 'water-stress',
        area: 'cooling',
        title: 'Water stress for evaporative cooling',
        severity: 'significant',
        finding: reason?.text ?? `Baseline water stress is ${label} (WRI Aqueduct): evaporative cooling would add a consumptive demand in a stressed basin.`,
        source: 'World Resources Institute (Aqueduct 4.0)',
        action: wetRecorded
          ? 'Secure a written water supply and discharge position, or move the cold end to dry or hybrid heat rejection.'
          : stageAction(context, {
              earlyScreening: 'Plan on dry or hybrid heat rejection, and do not present evaporative cooling to the water authority.',
              matureValidation: 'Specify dry or hybrid heat rejection and verify dry-cooler capacity at the site design dry bulb.',
            }),
        evidenceRefs: [{ kind: 'sed', section: 'waterStress' }, { kind: 'stage2-source', sourceId: 'wri-aqueduct-4' }],
      }));
    } else if (mediumHigh) {
      out.risks.push(evidenced({
        id: 'water-stress',
        area: 'cooling',
        title: 'Water stress for evaporative cooling',
        severity: 'noted',
        finding: `Baseline water stress is ${label} (WRI Aqueduct, 20–40% of supply withdrawn).`,
        source: 'World Resources Institute (Aqueduct 4.0)',
        action: 'Confirm the water supply and rights in writing before choosing evaporative cooling.',
        evidenceRefs: [{ kind: 'sed', section: 'waterStress' }, { kind: 'stage2-source', sourceId: 'wri-aqueduct-4' }],
      }));
    } else {
      out.clear.push('basin water stress');
    }
  }
  const finding = screeningWaterRisks(context.inputs.feasibility)[0];
  if (finding && context.inputs.feasibility) {
    out.risks.push(evidenced({
      id: 'water-supply-finding',
      area: 'cooling',
      title: 'Water supply for cooling',
      severity: finding.evidenceState === 'confirmed' ? 'significant' : 'noted',
      finding: finding.claim,
      source: 'Current feasibility screening',
      action: finding.suggestedFollowUp?.trim() || 'Confirm the water supply position in writing with the utility or water authority.',
      evidenceRefs: [{ kind: 'report', reportId: context.inputs.feasibility.id }],
    }));
  }
}

function dryCapacityRisk(context: RiskContext, out: RiskResult): void {
  const screen = context.inputs.dryCapacity;
  const dryEnd = context.coolingType === 'dry-cooler' || context.coolingType === 'hybrid' || context.coolingType === 'free-cooling';
  if (!screen?.screened || !dryEnd) return;
  if (screen.dryOnlyCandidate) {
    out.clear.push('dry-cooling temperature screen');
    return;
  }
  out.risks.push(evidenced({
    id: 'dry-capacity',
    area: 'cooling',
    title: 'Dry-cooling capacity at the design temperature',
    severity: 'noted',
    finding: `The published temperature screen does not establish full-duty dry capacity${screen.peakDryBulbProxyC != null ? ` at the ${screen.peakDryBulbProxyC.toFixed(1)}°C peak dry-bulb proxy` : ''}${screen.w45Status ? `; the W45 class is ${screen.w45Status}` : ''}.`,
    source: 'Water-use screen (published dry-design screen)',
    action: stageAction(context, {
      earlyScreening: 'Keep adiabatic assist or trim cooling in the cooling plan; vendor capacity is confirmed when equipment is selected.',
      matureValidation: 'Verify dry-cooler capacity with vendor curves at the site design dry bulb, and plan adiabatic assist or trim cooling if it falls short.',
    }),
    evidenceRefs: [{ kind: 'water-screen' }],
  }));
}

const SDC_ORDER = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

function siteHazardRisks(context: RiskContext, out: RiskResult): void {
  const flood = context.inputs.trusted.flood?.data;
  if (!flood) {
    out.risks.push(unassessed({
      id: 'flood',
      area: 'site',
      title: 'Flood exposure of power and cooling plant',
      finding: 'No FEMA flood screen is on the current record.',
      source: 'Current site evidence',
      action: 'Refresh the site evidence to load the FEMA flood screen.',
    }));
  } else if (flood.sfha) {
    const zone = flood.floodZone?.trim();
    out.risks.push(evidenced({
      id: 'flood',
      area: 'site',
      title: 'Flood exposure of power and cooling plant',
      severity: 'noted',
      finding: `The mapped site overlaps a FEMA Special Flood Hazard Area${zone ? ` (zone ${zone})` : ''}.`,
      source: 'FEMA National Flood Hazard Layer',
      action: 'Place generation, switchgear and cooling plant outside the SFHA or above the base flood elevation.',
      evidenceRefs: [{ kind: 'sed', section: 'flood' }],
    }));
  } else {
    out.clear.push('flood (FEMA)');
  }
  const seismic = context.inputs.trusted.seismic?.data;
  const sdc = seismic?.designCategory?.trim().toUpperCase().charAt(0);
  const index = sdc ? SDC_ORDER.indexOf(sdc as typeof SDC_ORDER[number]) : -1;
  // Seismic Design Category C and above is where the platform already asks
  // for site-specific seismic characterization (resolveEffectiveConclusion).
  if (index >= 2) {
    out.risks.push(evidenced({
      id: 'seismic',
      area: 'site',
      title: 'Seismic design of power and cooling plant',
      severity: index >= 3 ? 'significant' : 'noted',
      finding: `Seismic Design Category ${sdc} (USGS): seismic characterization is required.`,
      source: 'USGS seismic design maps',
      action: 'Commission a site-specific seismic hazard analysis and specify seismically qualified generation, electrical and cooling equipment.',
      evidenceRefs: [{ kind: 'sed', section: 'seismic' }],
    }));
  } else if (index >= 0) {
    out.clear.push(`seismic (SDC ${sdc})`);
  }
}

function orderRisks(risks: readonly PowerCoolingRisk[]): PowerCoolingRisk[] {
  const rank = (risk: PowerCoolingRisk): number => (
    risk.state === 'not-assessed' ? 2 : risk.severity === 'significant' ? 0 : 1
  );
  return risks
    .map((risk, index) => ({ risk, index }))
    .sort((left, right) => rank(left.risk) - rank(right.risk) || left.index - right.index)
    .map(({ risk }) => risk);
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────

/** Stage 2 facts come only from the validated snapshot and usable report adapters. */
function addMatureRisks(context: RiskContext, out: RiskResult): void {
  if (context.stage !== 'mature-validation') return;
  const scenario = context.inputs.promotedScenario;
  const actions: Record<NonNullable<typeof scenario>['result']['risks'][number]['id'], [string, string]> = {
    'n-1-shortfall': ['Firm capacity at N-1', 'Add a unit or storage so N-1 net capacity covers the critical load.'],
    'reserve-shortfall': ['Reserve margin', 'Increase reserve or reduce the critical load in the scenario.'],
    'contingency-concentration': ['Largest single contingency', 'Split the largest contingency group.'],
    'gas-capacity-mismatch': ['Gas demand vs documented supply', 'Contract firm transport for the scenario’s peak demand.'],
    'gas-interconnect-derate': ['Gas interconnect derate', 'Confirm delivery pressure and interconnect capacity with the pipeline operator.'],
    'water-source-mismatch': ['Water source vs cooling design', 'Align the cooling design with the recorded water source.'],
    'ccs-export-uncertainty': ['CO₂ export path', 'Confirm a CO₂ offtake or storage route.'],
    'receptor-noise-exceedance': ['Noise at receptors', 'Add attenuation or move units away from the failing receptors.'],
    'missing-vendor-permit-inputs': ['Vendor permit inputs', 'Obtain vendor emission and noise data for the chosen units.'],
  };
  if (scenario) for (const risk of scenario.result.risks ?? []) {
    const [title, defaultAction] = actions[risk.id];
    const input = scenario.input;
    const missingGas = risk.id === 'gas-capacity-mismatch' && input.availableGasCapacityDthPerDay == null;
    const missingWater = risk.id === 'water-source-mismatch' && input.availableWaterConsumptionGalPerDay == null;
    const missingNoise = risk.id === 'receptor-noise-exceedance' && ((input.receptors ?? []).length === 0 || (scenario.result.noise ?? []).length === 0 || (scenario.result.noise ?? []).some(receptor => receptor.verdict === 'indeterminate'));
    const action = missingGas ? 'Request firm transport capacity from the pipeline operator and record it in the scenario, so supply is checked against peak demand.'
      : missingWater ? 'Record the water source and its capacity so the scenario can check it against the plant’s water duty.'
      : missingNoise ? 'Obtain receptor distances and a noise threshold so the scenario can screen receptors.' : defaultAction;
    const missing = missingGas || missingWater || missingNoise || risk.id === 'missing-vendor-permit-inputs';
    out.risks.push((missing ? unassessed : evidenced)({ id: `scenario:${risk.id}`, area: risk.id.startsWith('water') ? 'cooling' : 'btm', title,
      severity: risk.severity === 'red' ? 'significant' : 'noted', finding: risk.message,
      source: `Promoted scenario “${scenario.name}”`, action, origin: 'mature-validation', evidenceRefs: [{ kind: 'scenario' }] }));
  }
  const supply = context.inputs.supplyChain;
  const binding = supply?.supplyChainAnalysis?.longLeadVsCod?.bindingConstraint;
  if (binding && supply) out.risks.push(evidenced({ id: 'supply-chain-binding', area: 'power', title: 'Long-lead equipment vs in-service date',
    severity: 'significant', finding: binding, source: 'Supply Chain Risk report', action: 'Confirm delivery slots against the in-service date or re-sequence the phases.',
    origin: 'mature-validation', evidenceRefs: [{ kind: 'report', reportId: supply.id }] }));
  const fit = context.inputs.testFit;
  if (fit?.testFitAnalysis?.generationFootprint?.acreageFitVerdict === 'fail') out.risks.push(evidenced({ id: 'test-fit-area', area: 'site',
    title: 'Plant does not fit the buildable area', severity: 'significant', finding: 'The Test Fit acreage screen fails for the generation plant.',
    source: 'Test Fit report', action: 'Revise the plant footprint or establish a larger buildable area.', origin: 'mature-validation', evidenceRefs: [{ kind: 'report', reportId: fit.id }] }));
}

export function buildPowerCoolingAnswers(inputs: PowerCoolingInputs): PowerCoolingAnswerSet {
  const intent = inputs.site.projectIntent;
  const recordedStrategy = inputs.site.typedPreferencesDefaults?.['power-availability']?.btmStrategy;
  // Stage 2 reads the SED directly. The brief only trusts a water-stress
  // section that passes its trust rules, so an untrusted one is withheld here
  // too — the cooling recommendation and the water rows then agree.
  const stage2Sed = inputs.sed && !inputs.trusted.waterStress
    ? { ...inputs.sed, waterStress: { ...inputs.sed.waterStress, data: null } }
    : inputs.sed;
  // Shared option constraints qualify the site's resource screen. Saved
  // usable screening outcomes remain advisory alongside these stances.
  const powerOptions = assessPowerOptions({
    intent,
    sed: stage2Sed,
    availability: inputs.availability,
    feasibility: inputs.feasibility,
    environmental: inputs.environmental,
    studyAreaAcres: inputs.studyAreaAcres,
    asOf: inputs.asOf,
  });
  const coolingOptions = assessCoolingOptions({ intent, sed: stage2Sed, feasibility: inputs.feasibility });
  const context: LineContext = {
    inputs, intent, recordedStrategy, powerOptions, coolingOptions,
    screeningRecommendations: deriveScreeningArchitectureRecommendations(inputs.feasibility, inputs.site.id),
  };

  const strategy = btmArchitectureLine(context);
  const powerType = powerTypeLine(context, strategy);
  const coolingType = coolingTypeLine(context);
  const byId: Record<PowerCoolingLineId, PowerCoolingLine> = {
    'btm-architecture': strategy.line,
    'power-type': powerType.line,
    'dc-power': dcPowerLine(context),
    'cooling-strategy': coolingStrategyLine(context),
    'cooling-type': coolingType.line,
  };
  const lines = LINE_ORDER.map(id => {
    const line = byId[id];
    if (line.openIn || line.basis === 'not-determined') {
      const mature = inputs.stage === 'mature-validation';
      line.route = mature || line.openIn === 'mature-validation'
        ? { kind: 'stage2-decision', id: id.startsWith('cooling') ? 'cooling' : id === 'dc-power' ? 'build-phases' : 'power-supply' }
        : id === 'dc-power' ? line.basis === 'not-determined'
          ? { kind: 'phase', phase: 'build' } : { kind: 'intent-field', field: 'powerRequirementMW' }
        : id === 'cooling-strategy' ? { kind: 'intent-field', field: 'workloadProfile' }
        : id === 'btm-architecture' ? { kind: 'phase', phase: 'build' }
        : { kind: 'phase', phase: 'verify' };
      if (mature) line.nextStep ??= `Record the ${id.startsWith('cooling') ? 'cooling' : 'power'} decision in Intent › Stage 2 decisions.`;
      if (id === 'btm-architecture' && line.openIn === 'early-screening') {
        line.spokenLabel = 'The BTM architecture';
        line.settlesWith = 'the usable-land, fuel and cooling-water screen';
      }
    }
    return line;
  });

  const riskContext: RiskContext = {
    inputs,
    intent,
    stage: inputs.stage ?? 'early-screening',
    strategy: strategy.strategy,
    supplyOptions: powerType.options,
    phaseOptions: powerType.phaseOptions ?? null,
    powerOptions,
    coolingOptions,
    coolingType: coolingType.value,
  };
  const result: RiskResult = { risks: [], clear: [] };
  gridCapacityRisk(riskContext, result);
  interconnectionRisk(riskContext, result);
  airPermittingRisk(riskContext, result);
  standbyAirPermittingRisk(riskContext, result);
  gasSupplyRisk(riskContext, result);
  leadTimeRisks(riskContext, result);
  waterStressRisks(riskContext, result);
  dryCapacityRisk(riskContext, result);
  siteHazardRisks(riskContext, result);
  addMatureRisks(riskContext, result);
  const risks = orderRisks(result.risks).map(risk => {
    risk.origin ??= 'early-screening';
    risk.route ??= risk.id === 'interconnection' ? { kind: 'intent-field', field: 'interconnectionStatus.queueStatus' }
      : risk.id === 'gas-supply' ? { kind: 'intent-field', field: 'fuelAccess.supply' }
      : risk.origin === 'mature-validation' ? { kind: 'generation-workbench' }
      : { kind: 'phase', phase: 'verify' };
    if (risk.id === 'water-stress') {
      risk.spokenResponse = riskContext.stage === 'early-screening' ? 'screening recommends dry or hybrid heat rejection in response'
        : intent?.bopHeatRejection === 'dry-cooler' ? 'the recorded design keeps dry heat rejection, with dry-cooler capacity still to be verified against vendor data' : 'dry or hybrid heat rejection is specified in response';
      if (riskContext.stage === 'mature-validation' && intent?.bopHeatRejection === 'dry-cooler') risk.action = 'Keep dry heat rejection in the design and verify dry-cooler capacity at the site design dry bulb.';
    } else if (risk.id === 'air-permitting') risk.spokenResponse = 'on-site combustion needs its permitting basis confirmed';
    else if (risk.id === 'grid-capacity') risk.spokenResponse = 'the shortfall shapes the supply architecture';
    else if (risk.id.startsWith('lead-time:')) risk.spokenResponse = riskContext.stage === 'early-screening' ? 'equipment lead time shapes the early-phase supply' : 'delivery slots are being confirmed against the phase dates';
    return risk;
  });

  const lineBases: CallReadyAnswerBasis[] = [];
  if (lines.some(line => line.basis === 'recorded')) lineBases.push('site-fact');
  if (lines.some(line => line.evidenceRefs.some(ref => ref.kind === 'scenario'))) lineBases.push('modelled-screening');
  if (lines.some(line => line.basis === 'recommended')) {
    lineBases.push('modelled-screening');
    const refs = lines.filter(line => line.basis === 'recommended').flatMap(line => line.evidenceRefs);
    if (refs.some(ref => ref.kind === 'sed')) lineBases.push('public-data');
    if (refs.some(ref => ref.kind === 'report')) lineBases.push('report-finding');
  }
  const evidencedRisks = risks.filter(risk => risk.state === 'evidenced');
  const riskRefs = evidencedRisks.flatMap(risk => risk.evidenceRefs);
  const riskBases: CallReadyAnswerBasis[] = [
    ...(riskRefs.some(ref => ref.kind === 'site') ? ['site-fact' as const] : []),
    ...(riskRefs.some(ref => ref.kind === 'sed') || result.clear.length > 0 ? ['public-data' as const] : []),
    ...(riskRefs.some(ref => ref.kind === 'report') ? ['report-finding' as const] : []),
    ...(riskRefs.some(ref => ref.kind === 'water-screen') ? ['modelled-screening' as const] : []),
    ...(riskRefs.some(ref => ref.kind === 'stage2-source') ? ['public-record' as const] : []),
  ];

  const resourceStatements = inputs.sed ? siteResourceViewFromSed(inputs.sed)
    : readSiteResourceView(inputs.feasibility?.siteDiligenceEvidence?.resourceStatements);
  return {
    lines,
    risks,
    clearTopics: result.clear,
    lineBases: [...new Set(lineBases)],
    riskBases,
    coolingType: coolingType.value,
    ...(resourceStatements ? { resourceStatements } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Answer framing: status, next step, presenter guidance, spoken summary
// ─────────────────────────────────────────────────────────────────────────────

/** A Stage 1 gap: a line that can be closed during early screening. */
export function isStage1Gap(line: Pick<PowerCoolingLine, 'openIn'>): boolean {
  return line.openIn === 'early-screening';
}

export interface PowerCoolingAnswerFraming {
  status: 'ready' | 'partial' | 'unavailable';
  /** The composed spoken form (the lines are the on-screen answer). */
  summary: string;
  nextInput?: string;
  /** The work phase whose tab closes `nextInput`. */
  nextInputPhase?: 'build' | 'verify';
  route?: CallReadyRoute;
  /** One line of presenter guidance; omitted when the next step already says it. */
  recommendation?: CallReadyRecommendation;
}

interface SpokenTrim {
  phases: boolean;
  because: boolean;
  compactPowerType: boolean;
  powerType: boolean;
}

const SPOKEN_TRIMS: readonly SpokenTrim[] = [
  { phases: true, because: true, compactPowerType: false, powerType: true },
  { phases: false, because: true, compactPowerType: false, powerType: true },
  { phases: false, because: false, compactPowerType: false, powerType: true },
  { phases: false, because: false, compactPowerType: true, powerType: true },
  { phases: false, because: false, compactPowerType: true, powerType: false },
];

/**
 * Recorded parts first ("The project has recorded …"), recommended parts
 * after ("screening recommends …"): one sentence for power (architecture,
 * power type, for the load) and one for cooling (rack loop, cold end, with the
 * water-stress reason when the cold end is recommended), then the screening
 * disclaimer when anything is recommended. Lines not yet determined are
 * omitted, except the load.
 */
function composeSpokenSummary(byId: ReadonlyMap<PowerCoolingLineId, PowerCoolingLine>, trim: SpokenTrim): string {
  const determined = (id: PowerCoolingLineId): PowerCoolingLine | null => {
    const line = byId.get(id);
    return line && line.basis !== 'not-determined' && line.spoken ? line : null;
  };
  const architecture = determined('btm-architecture');
  let powerType = trim.powerType ? determined('power-type') : null;
  const load = determined('dc-power');
  const loop = determined('cooling-strategy');
  const sink = determined('cooling-type');
  // "a grid-only architecture" already says grid import.
  if (architecture && powerType && architecture.spoken === STRATEGY_SPOKEN['grid-only']
    && powerType.basis === architecture.basis && /^grid import/.test(powerType.spoken ?? '')) {
    powerType = null;
  }
  const powerTypeSpoken = (line: PowerCoolingLine): string => (
    trim.compactPowerType && line.basis === 'recorded' && /→/.test(line.value)
      ? 'phase-by-phase power sources'
      : line.spoken!
  );
  const loadText = load
    ? trim.phases ? load.spoken! : load.spoken!.replace(/ in \d+ phases$/, '')
    : null;

  type Part = { line: PowerCoolingLine; text: string };
  const texts = (parts: readonly Part[], basis: PowerCoolingLineBasis): string[] => (
    parts.filter(part => part.line.basis === basis).map(part => part.text)
  );
  const sentence = (recorded: readonly string[], recommended: string | null, tail = ''): string | null => {
    if (recorded.length === 0 && !recommended) return null;
    const recordedClause = recorded.length > 0 ? `The project has recorded ${joinList(recorded)}` : null;
    const recommendedClause = recommended
      ? `${recordedClause ? 'screening' : 'Screening'} recommends ${recommended}`
      : null;
    return `${[recordedClause, recommendedClause].filter(Boolean).join('; ')}${tail}.`;
  };

  // Power: the architecture "with" the power type, "for" the load.
  const powerParts: Part[] = [
    ...(architecture ? [{ line: architecture, text: architecture.spoken! }] : []),
    ...(powerType ? [{ line: powerType, text: powerTypeSpoken(powerType) }] : []),
  ];
  const powerRecorded = texts(powerParts, 'recorded');
  const powerRecommended = texts(powerParts, 'recommended');
  // Cooling: the rack loop and the cold end, with the water-stress reason
  // when the cold end is recommended.
  const coolingParts: Part[] = [
    ...(loop ? [{ line: loop, text: loop.spoken! }] : []),
    ...(sink
      ? [{
          line: sink,
          text: `${sink.spoken!}${trim.because && sink.basis === 'recommended' && sink.spokenReason ? ` because ${sink.spokenReason}` : ''}`,
        }]
      : []),
  ];
  // The rack loop "with" its cold end ("direct liquid cooling with dry coolers").
  const coolingRecorded = texts(coolingParts, 'recorded');
  const coolingRecordedText = coolingRecorded.length > 0 ? coolingRecorded.join(' with ') : null;
  const coolingRecommended = texts(coolingParts, 'recommended');
  const coolingRecommendedText = coolingRecommended.length > 0 ? coolingRecommended.join(' with ') : null;

  const sentences: Array<string | null> = [];
  if (powerParts.length > 0) {
    sentences.push(sentence(
      powerRecorded.length > 0 ? [powerRecorded.join(' with ')] : [],
      powerRecommended.length > 0 ? powerRecommended.join(' with ') : null,
      loadText && load?.basis === 'recorded' ? ` for ${loadText}` : '',
    ));
    if (load?.basis === 'recommended') sentences.push(sentence([], loadText));
    sentences.push(load ? null : 'The data center load is not yet recorded.');
    sentences.push(sentence(coolingRecordedText ? [coolingRecordedText] : [], coolingRecommendedText));
  } else if (loadText) {
    // Only the load on the power side: one sentence with the recorded cooling.
    sentences.push(sentence(
      [...(load?.basis === 'recorded' ? [loadText] : []), ...(coolingRecordedText ? [coolingRecordedText] : [])],
      joinList([...(load?.basis === 'recommended' ? [loadText] : []), ...(coolingRecommendedText ? [coolingRecommendedText] : [])]) || null,
    ));
  } else {
    sentences.push('The data center load is not yet recorded.');
    sentences.push(sentence(coolingRecordedText ? [coolingRecordedText] : [], coolingRecommendedText));
  }

  const openArchitecture = byId.get('btm-architecture');
  if (openArchitecture?.spokenLabel && openArchitecture.settlesWith) sentences.splice(1, 0, `${openArchitecture.spokenLabel} is not yet determined; it settles with ${openArchitecture.settlesWith}.`);

  const recommendedCount = [...byId.values()].filter(line => line.basis === 'recommended').length;
  const disclaimer = recommendedCount === 0
    ? null
    : recommendedCount === 1
      ? 'This is a screening recommendation, not an adopted design or a utility commitment.'
      : 'These are screening recommendations, not an adopted design or a utility commitment.';

  return [...sentences, disclaimer].filter((part): part is string => part != null).join(' ');
}

/**
 * Status, next step, presenter guidance and spoken summary for the lead.
 *
 * Status follows the Stage 1 gap rule: `unavailable` when no line is recorded
 * or recommended, `ready` when no line is a Stage 1 gap (a Stage 2 decision
 * field left open is not a screening gap), `partial` otherwise.
 *
 * Next step, first match wins: the first Stage 1 gap in resolution order; with
 * a usable Screening Assessment and something recommended, review it in
 * Intent › Stage 2 decisions; without one, run it. `screeningAssessmentUsable`
 * stands in for "early screening is complete" until the brief carries the
 * decision stage.
 */
export function framePowerCoolingAnswer(
  lines: readonly PowerCoolingLine[],
  options: { screeningAssessmentUsable: boolean; screeningComplete?: boolean; stage?: CallReadyBriefStage },
): PowerCoolingAnswerFraming {
  const byId = new Map(lines.map(line => [line.id, line]));
  const gaps = POWER_COOLING_RESOLUTION_ORDER
    .map(id => byId.get(id))
    .filter((line): line is PowerCoolingLine => line != null && (options.stage === 'mature-validation' ? line.openIn != null || line.basis === 'not-determined' : isStage1Gap(line)));
  const recommended = lines.filter(line => line.basis === 'recommended');
  const status: PowerCoolingAnswerFraming['status'] = lines.every(line => line.basis === 'not-determined')
    ? 'unavailable'
    : gaps.length === 0 ? 'ready' : 'partial';

  const firstGap = gaps.find(line => line.nextStep);
  const powerTypeRecorded = byId.get('power-type')?.basis === 'recorded';
  const next: Pick<PowerCoolingAnswerFraming, 'nextInput' | 'nextInputPhase'> = firstGap
    ? { nextInput: firstGap.nextStep, nextInputPhase: firstGap.route?.kind === 'phase' && firstGap.route.phase === 'build' ? 'build' : 'verify' }
    : (options.screeningComplete ?? options.screeningAssessmentUsable)
      ? recommended.length > 0
        ? {
            nextInput: `Review the recommended ${joinList(recommended.map(line => lowerFirst(line.label)))} in Intent › Stage 2 decisions and accept ${recommended.length === 1 ? 'it' : 'them'} or record your own.`,
            nextInputPhase: 'verify',
          }
        : {}
      : {
          nextInput: powerTypeRecorded
            ? 'Run the Early Screening Assessment.'
            : 'Run the Early Screening Assessment; its development pathway sharpens the power-type recommendation.',
          nextInputPhase: 'build',
        };

  const recommendation: CallReadyRecommendation | undefined = recommended.length > 0
    ? { tone: 'caution', text: 'Say “screening recommends” for the Recommended lines; they are not an adopted design or a utility commitment.' }
    : lines.every(line => line.basis === 'recorded')
      ? { tone: 'proceed', text: 'Present these as the project\'s recorded basis; they are not approved designs or utility commitments.' }
      : undefined;

  // W8: the spoken form (template wrapper included) stays within the limit;
  // optional detail drops first (phase count, the water-stress reason, a
  // multi-phase power-type list), never the disclaimer.
  const wrapper = CALL_READY_SPOKEN_STATE_TEMPLATES[status].replace('{{summary}}', '');
  const budget = POWER_COOLING_SPOKEN_WORD_LIMIT - wordCount(wrapper);
  const candidates = SPOKEN_TRIMS.map(trim => composeSpokenSummary(byId, trim));
  const summary = candidates.find(candidate => wordCount(candidate) <= budget) ?? candidates[candidates.length - 1]!;

  return {
    status,
    summary,
    ...next,
    ...(next.nextInput ? { route: firstGap?.route ?? ((options.screeningComplete ?? options.screeningAssessmentUsable) ? { kind: 'stage2-decision' as const, id: recommended.some(line => line.id === 'btm-architecture' || line.id === 'power-type') ? 'power-supply' as const : 'cooling' as const } : { kind: 'phase' as const, phase: 'build' as const }) } : {}),
    ...(recommendation ? { recommendation } : {}),
  };
}

/** Spoken forms reused by the Details "Screening what-if" row. */
export function spokenCoolingStrategy(value: 'air' | 'liquid' | 'hybrid'): string {
  return COOLING_STRATEGY_SPOKEN[value];
}

export function spokenHeatRejection(value: Exclude<BopHeatRejection, 'unknown'>): string {
  return HEAT_REJECTION_SPOKEN[value];
}
